/**
 * Dungeon Equipment Items
 *
 * Equipment that can drop from dungeon enemies and be equipped by the player.
 * Each slot has items across all 5 rarity tiers.
 */

// ============================================================
// WEAPONS (21 total: 3 common, 3 uncommon, 3 rare, 3 epic, 6 legendary, 6 mythic)
// ============================================================
export const WEAPONS = {
  // Common Weapons (3) - Buffed for 15× enemy scaling
  rustyDagger: {
    id: 'rustyDagger',
    name: 'Rusty Dagger',
    icon: '🗡️',
    slot: 'weapon',
    rarity: 'common',
    description: 'A worn dagger, barely sharp enough to cut.',
    stats: {
      attack: 8,
      critChance: 0.03,
    },
  },
  woodenClub: {
    id: 'woodenClub',
    name: 'Wooden Club',
    icon: '🏏',
    slot: 'weapon',
    rarity: 'common',
    description: 'A simple club carved from hardwood.',
    stats: {
      attack: 10,
      critChance: 0.02,
    },
  },
  brokenSword: {
    id: 'brokenSword',
    name: 'Broken Sword',
    icon: '⚔️',
    slot: 'weapon',
    rarity: 'common',
    description: 'Half a blade is better than none.',
    stats: {
      attack: 12,
      critChance: 0.04,
    },
  },

  // Uncommon Weapons (3) - Buffed for 15× enemy scaling
  steelSword: {
    id: 'steelSword',
    name: 'Steel Sword',
    icon: '⚔️',
    slot: 'weapon',
    rarity: 'uncommon',
    description: 'A well-crafted blade of tempered steel.',
    stats: {
      attack: 18,
      critChance: 0.06,
      critDamage: 0.20,
    },
  },
  huntingBow: {
    id: 'huntingBow',
    name: 'Hunting Bow',
    icon: '🏹',
    slot: 'weapon',
    rarity: 'uncommon',
    description: 'A reliable bow for hunting game... or enemies.',
    stats: {
      attack: 16,
      critChance: 0.10,
      critDamage: 0.15,
    },
  },
  warHammer: {
    id: 'warHammer',
    name: 'War Hammer',
    icon: '🔨',
    slot: 'weapon',
    rarity: 'uncommon',
    description: 'Crushes armor and bones with equal ease.',
    stats: {
      attack: 20,
      critChance: 0.05,
      critDamage: 0.25,
    },
  },

  // Rare Weapons (3) - Buffed for 15× enemy scaling
  flamingSword: {
    id: 'flamingSword',
    name: 'Flaming Sword',
    icon: '🔥',
    slot: 'weapon',
    rarity: 'rare',
    description: 'A blade wreathed in eternal flames.',
    stats: {
      attack: 30,
      critChance: 0.10,
      critDamage: 0.40,
    },
  },
  crystalBow: {
    id: 'crystalBow',
    name: 'Crystal Bow',
    icon: '🏹',
    slot: 'weapon',
    rarity: 'rare',
    description: 'Arrows fired from this bow never miss their mark.',
    stats: {
      attack: 28,
      critChance: 0.15,
      critDamage: 0.45,
    },
  },
  thunderAxe: {
    id: 'thunderAxe',
    name: 'Thunder Axe',
    icon: '⚡',
    slot: 'weapon',
    rarity: 'rare',
    description: 'Each strike echoes with the sound of thunder.',
    stats: {
      attack: 32,
      critChance: 0.10,
      critDamage: 0.50,
    },
  },

  // Epic Weapons (3) - Buffed for 15× enemy scaling
  dragonslayerBlade: {
    id: 'dragonslayerBlade',
    name: 'Dragonslayer Blade',
    icon: '⚔️',
    slot: 'weapon',
    rarity: 'epic',
    description: 'Forged to slay dragons, feared by all creatures.',
    stats: {
      attack: 55,
      critChance: 0.15,
      critDamage: 0.75,
    },
  },
  voidTrident: {
    id: 'voidTrident',
    name: 'Void Trident',
    icon: '🔱',
    slot: 'weapon',
    rarity: 'epic',
    description: 'A weapon from beyond reality itself.',
    stats: {
      attack: 50,
      critChance: 0.20,
      critDamage: 0.65,
    },
  },
  starfallenBow: {
    id: 'starfallenBow',
    name: 'Starfallen Bow',
    icon: '🏹',
    slot: 'weapon',
    rarity: 'epic',
    description: 'Its arrows are fragments of fallen stars.',
    stats: {
      attack: 48,
      critChance: 0.22,
      critDamage: 0.70,
    },
  },

  // Legendary Weapons (6 balanced weapons: 75-95 ATK) - Buffed for 15× scaling
  soulreaver: {
    id: 'soulreaver',
    name: 'Soulreaver',
    icon: '⚔️',
    slot: 'weapon',
    rarity: 'legendary',
    description: 'A blade that devours the souls of the slain.',
    stats: {
      attack: 85,
      critChance: 0.25,
      critDamage: 1.20,
    },
  },
  cosmicScythe: {
    id: 'cosmicScythe',
    name: 'Cosmic Scythe',
    icon: '🌌',
    slot: 'weapon',
    rarity: 'legendary',
    description: 'Wielded by the harbinger of the end times.',
    stats: {
      attack: 82,
      critChance: 0.28,
      critDamage: 1.10,
    },
  },
  infinityStaff: {
    id: 'infinityStaff',
    name: 'Infinity Staff',
    icon: '✨',
    slot: 'weapon',
    rarity: 'legendary',
    description: 'Channels the infinite power of creation itself.',
    stats: {
      attack: 75,
      critChance: 0.30,
      critDamage: 1.30,
    },
  },
  voidBlade: {
    id: 'voidBlade',
    name: 'Void Blade',
    icon: '🌑',
    slot: 'weapon',
    rarity: 'legendary',
    description: 'A blade forged from crystallized void energy.',
    stats: {
      attack: 90,
      critChance: 0.22,
      critDamage: 1.15,
      defense: 15,
    },
  },
  starforgedAxe: {
    id: 'starforgedAxe',
    name: 'Starforged Axe',
    icon: '⚡',
    slot: 'weapon',
    rarity: 'legendary',
    description: 'An axe forged in the heart of a supernova.',
    stats: {
      attack: 95,
      critChance: 0.20,
      critDamage: 1.40,
    },
  },
  eternityBow: {
    id: 'eternityBow',
    name: 'Eternity Bow',
    icon: '🏹',
    slot: 'weapon',
    rarity: 'legendary',
    description: 'Its arrows pierce through time itself.',
    stats: {
      attack: 88,
      critChance: 0.26,
      critDamage: 1.35,
      speed: 1,
    },
  },

  // Mythic Weapons (6 ultimate weapons: 130-160 ATK) - Buffed for 15× scaling
  firebrand: {
    id: 'firebrand',
    name: 'Firebrand',
    icon: '🔥',
    sprite: 'firebrand.png',
    slot: 'weapon',
    rarity: 'mythic',
    description: 'A legendary blade forged in the heart of a dying star. Burns with eternal hellfire.',
    stats: {
      attack: 160,
      critChance: 0.40,
      critDamage: 2.50,
      health: 300,
    },
  },
  frostblade: {
    id: 'frostblade',
    name: 'Frostblade',
    icon: '❄️',
    sprite: 'frostblade.png',
    slot: 'weapon',
    rarity: 'mythic',
    description: 'An ancient blade frozen in eternal ice. Slows time itself with each strike.',
    stats: {
      attack: 150,
      critChance: 0.45,
      critDamage: 2.20,
      defense: 40,
      speed: 2,
    },
  },
  natureweaver: {
    id: 'natureweaver',
    name: 'Natureweaver',
    icon: '🌿',
    sprite: 'natureweaver.png',
    slot: 'weapon',
    rarity: 'mythic',
    description: 'A living staff that commands the forces of nature. Heals the wielder with each strike.',
    stats: {
      attack: 130,
      critChance: 0.35,
      critDamage: 1.80,
      health: 700,
      defense: 35,
    },
  },
  voidreaper: {
    id: 'voidreaper',
    name: 'Voidreaper',
    icon: '🕳️',
    slot: 'weapon',
    rarity: 'mythic',
    description: 'Harvests souls from beyond the veil.',
    stats: {
      attack: 145,
      critChance: 0.42,
      critDamage: 2.30,
      health: 150,
    },
  },
  cosmicEnder: {
    id: 'cosmicEnder',
    name: 'Cosmic Ender',
    icon: '🌌',
    slot: 'weapon',
    rarity: 'mythic',
    description: 'The blade that ends universes.',
    stats: {
      attack: 155,
      critChance: 0.38,
      critDamage: 2.60,
    },
  },
  eternityEdge: {
    id: 'eternityEdge',
    name: 'Eternity Edge',
    icon: '⚡',
    slot: 'weapon',
    rarity: 'mythic',
    description: 'Forged at the end of time itself.',
    stats: {
      attack: 140,
      critChance: 0.48,
      critDamage: 2.00,
      defense: 50,
      speed: 1,
    },
  },
};

// ============================================================
// ARMOR (15 total: 3 per rarity)
// ============================================================
export const ARMOR = {
  // Common Armor (3)
  leatherVest: {
    id: 'leatherVest',
    name: 'Leather Vest',
    icon: '🧥',
    slot: 'top',
    rarity: 'common',
    description: 'Basic protection from cuts and scrapes.',
    stats: {
      defense: 2,
      health: 15,
    },
  },
  clothTunic: {
    id: 'clothTunic',
    name: 'Cloth Tunic',
    icon: '👕',
    slot: 'top',
    rarity: 'common',
    description: 'Light and comfortable, but offers little defense.',
    stats: {
      defense: 1,
      health: 20,
    },
  },
  paddedArmor: {
    id: 'paddedArmor',
    name: 'Padded Armor',
    icon: '🦺',
    slot: 'top',
    rarity: 'common',
    description: 'Quilted layers provide modest protection.',
    stats: {
      defense: 3,
      health: 12,
    },
  },

  // Uncommon Armor (3)
  chainmail: {
    id: 'chainmail',
    name: 'Chainmail',
    icon: '🛡️',
    slot: 'top',
    rarity: 'uncommon',
    description: 'Interlocking metal rings protect vital areas.',
    stats: {
      defense: 5,
      health: 30,
    },
  },
  scaleArmor: {
    id: 'scaleArmor',
    name: 'Scale Armor',
    icon: '🐉',
    slot: 'top',
    rarity: 'uncommon',
    description: 'Armor made from overlapping metal scales.',
    stats: {
      defense: 6,
      health: 25,
    },
  },
  studiedLeather: {
    id: 'studdedLeather',
    name: 'Studded Leather',
    icon: '🧥',
    slot: 'top',
    rarity: 'uncommon',
    description: 'Reinforced leather with metal studs.',
    stats: {
      defense: 4,
      health: 35,
    },
  },

  // Rare Armor (3)
  plateArmor: {
    id: 'plateArmor',
    name: 'Plate Armor',
    icon: '🛡️',
    slot: 'top',
    rarity: 'rare',
    description: 'Full body protection of solid steel plates.',
    stats: {
      defense: 10,
      health: 55,
    },
  },
  dragonscale: {
    id: 'dragonscale',
    name: 'Dragonscale Mail',
    icon: '🐲',
    slot: 'top',
    rarity: 'rare',
    description: 'Armor crafted from actual dragon scales.',
    stats: {
      defense: 9,
      health: 60,
    },
  },
  enchantedRobes: {
    id: 'enchantedRobes',
    name: 'Enchanted Robes',
    icon: '👘',
    slot: 'top',
    rarity: 'rare',
    description: 'Magically woven robes that deflect blows.',
    stats: {
      defense: 7,
      health: 65,
    },
  },

  // Epic Armor (3)
  titaniumPlate: {
    id: 'titaniumPlate',
    name: 'Titanium Plate',
    icon: '⚜️',
    slot: 'top',
    rarity: 'epic',
    description: 'Impossibly light yet unbreakably strong.',
    stats: {
      defense: 16,
      health: 95,
    },
  },
  celestialRobes: {
    id: 'celestialRobes',
    name: 'Celestial Robes',
    icon: '✨',
    slot: 'top',
    rarity: 'epic',
    description: 'Woven from stardust and moonlight.',
    stats: {
      defense: 14,
      health: 105,
    },
  },
  demonhideArmor: {
    id: 'demonhideArmor',
    name: 'Demonhide Armor',
    icon: '😈',
    slot: 'top',
    rarity: 'epic',
    description: 'Tanned from the hide of a greater demon.',
    stats: {
      defense: 15,
      health: 100,
    },
  },

  // Legendary Armor (3)
  eternalGuardian: {
    id: 'eternalGuardian',
    name: 'Eternal Guardian',
    icon: '🛡️',
    slot: 'top',
    rarity: 'legendary',
    description: 'Armor that has protected heroes across the ages.',
    stats: {
      defense: 28,
      health: 175,
    },
  },
  voidPlate: {
    id: 'voidPlate',
    name: 'Void Plate',
    icon: '🌌',
    slot: 'top',
    rarity: 'legendary',
    description: 'Forged in the space between realities.',
    stats: {
      defense: 30,
      health: 160,
    },
  },
  divineMail: {
    id: 'divineMail',
    name: 'Divine Mail',
    icon: '⚜️',
    slot: 'top',
    rarity: 'legendary',
    description: 'A gift from the gods themselves.',
    stats: {
      defense: 26,
      health: 190,
    },
  },
};

// ============================================================
// HELMETS (15 total: 3 per rarity)
// ============================================================
export const HELMETS = {
  // Common Helmets (3)
  leatherCap: {
    id: 'leatherCap',
    name: 'Leather Cap',
    icon: '🧢',
    slot: 'helmet',
    rarity: 'common',
    description: 'Protects your head from light blows.',
    stats: {
      health: 10,
      defense: 1,
    },
  },
  ironHelmet: {
    id: 'ironHelmet',
    name: 'Iron Helmet',
    icon: '⛑️',
    slot: 'helmet',
    rarity: 'common',
    description: 'A basic iron helm, dented but functional.',
    stats: {
      health: 8,
      defense: 2,
    },
  },
  woodenMask: {
    id: 'woodenMask',
    name: 'Wooden Mask',
    icon: '🎭',
    slot: 'helmet',
    rarity: 'common',
    description: 'A ceremonial mask that offers minimal protection.',
    stats: {
      health: 12,
      defense: 1,
    },
  },

  // Uncommon Helmets (3)
  steelHelm: {
    id: 'steelHelm',
    name: 'Steel Helm',
    icon: '⛑️',
    slot: 'helmet',
    rarity: 'uncommon',
    description: 'A well-crafted steel helmet with a visor.',
    stats: {
      health: 22,
      defense: 3,
    },
  },
  knightHelmet: {
    id: 'knightHelmet',
    name: 'Knight Helmet',
    icon: '👑',
    slot: 'helmet',
    rarity: 'uncommon',
    description: 'Full-face protection for the noble warrior.',
    stats: {
      health: 25,
      defense: 4,
    },
  },
  arcaneHood: {
    id: 'arcaneHood',
    name: 'Arcane Hood',
    icon: '🧙',
    slot: 'helmet',
    rarity: 'uncommon',
    description: 'A hood imbued with protective enchantments.',
    stats: {
      health: 20,
      defense: 3,
    },
  },

  // Rare Helmets (3)
  dragonHelm: {
    id: 'dragonHelm',
    name: 'Dragon Helm',
    icon: '🐲',
    slot: 'helmet',
    rarity: 'rare',
    description: 'Shaped like a dragon\'s head, fearsome and strong.',
    stats: {
      health: 42,
      defense: 6,
    },
  },
  crownOfThrones: {
    id: 'crownOfThrones',
    name: 'Crown of Thrones',
    icon: '👑',
    slot: 'helmet',
    rarity: 'rare',
    description: 'A crown worn by ancient warrior kings.',
    stats: {
      health: 38,
      defense: 5,
    },
  },
  crystalCirclet: {
    id: 'crystalCirclet',
    name: 'Crystal Circlet',
    icon: '💎',
    slot: 'helmet',
    rarity: 'rare',
    description: 'A delicate circlet that shields the mind.',
    stats: {
      health: 45,
      defense: 4,
    },
  },

  // Epic Helmets (3)
  titanHelm: {
    id: 'titanHelm',
    name: 'Titan Helm',
    icon: '⛑️',
    slot: 'helmet',
    rarity: 'epic',
    description: 'Forged by giants, sized for mortals.',
    stats: {
      health: 68,
      defense: 10,
    },
  },
  voidMask: {
    id: 'voidMask',
    name: 'Void Mask',
    icon: '👹',
    slot: 'helmet',
    rarity: 'epic',
    description: 'Those who wear it see into the abyss.',
    stats: {
      health: 72,
      defense: 9,
    },
  },
  celestialCrown: {
    id: 'celestialCrown',
    name: 'Celestial Crown',
    icon: '👑',
    slot: 'helmet',
    rarity: 'epic',
    description: 'A crown blessed by the stars above.',
    stats: {
      health: 75,
      defense: 8,
    },
  },

  // Legendary Helmets (3)
  helmOfEternity: {
    id: 'helmOfEternity',
    name: 'Helm of Eternity',
    icon: '⛑️',
    slot: 'helmet',
    rarity: 'legendary',
    description: 'Worn by immortal heroes throughout time.',
    stats: {
      health: 125,
      defense: 17,
    },
  },
  crownOfGods: {
    id: 'crownOfGods',
    name: 'Crown of Gods',
    icon: '👑',
    slot: 'helmet',
    rarity: 'legendary',
    description: 'Only the truly divine may wear this crown.',
    stats: {
      health: 135,
      defense: 15,
    },
  },
  infinityMask: {
    id: 'infinityMask',
    name: 'Infinity Mask',
    icon: '💎',
    slot: 'helmet',
    rarity: 'legendary',
    description: 'See all possible futures and pasts.',
    stats: {
      health: 140,
      defense: 14,
    },
  },
};

// ============================================================
// BOOTS (15 total: 3 per rarity)
// ============================================================
export const BOOTS = {
  // Common Boots (3)
  leatherBoots: {
    id: 'leatherBoots',
    name: 'Leather Boots',
    icon: '👢',
    slot: 'boots',
    rarity: 'common',
    description: 'Simple boots for basic foot protection.',
    stats: {
      speed: 0,
      defense: 1,
    },
  },
  woodenSandals: {
    id: 'woodenSandals',
    name: 'Wooden Sandals',
    icon: '🩴',
    slot: 'boots',
    rarity: 'common',
    description: 'Light and airy, but prone to splinters.',
    stats: {
      speed: 1,
      defense: 0,
    },
  },
  ironSabatons: {
    id: 'ironSabatons',
    name: 'Iron Sabatons',
    icon: '👢',
    slot: 'boots',
    rarity: 'common',
    description: 'Heavy iron boots, slow but protective.',
    stats: {
      speed: 0,
      defense: 2,
    },
  },

  // Uncommon Boots (3)
  steelBoots: {
    id: 'steelBoots',
    name: 'Steel Boots',
    icon: '👢',
    slot: 'boots',
    rarity: 'uncommon',
    description: 'Well-balanced steel footwear.',
    stats: {
      speed: 1,
      defense: 3,
    },
  },
  swiftRunners: {
    id: 'swiftRunners',
    name: 'Swift Runners',
    icon: '👟',
    slot: 'boots',
    rarity: 'uncommon',
    description: 'Enchanted boots that increase your speed.',
    stats: {
      speed: 1,
      defense: 2,
    },
  },
  warBoots: {
    id: 'warBoots',
    name: 'War Boots',
    icon: '🥾',
    slot: 'boots',
    rarity: 'uncommon',
    description: 'Reinforced boots for the battlefield.',
    stats: {
      speed: 0,
      defense: 4,
    },
  },

  // Rare Boots (3)
  dragonrider: {
    id: 'dragonrider',
    name: 'Dragonrider Boots',
    icon: '🐉',
    slot: 'boots',
    rarity: 'rare',
    description: 'Boots worn by legendary dragon riders.',
    stats: {
      speed: 2,
      defense: 4,
    },
  },
  shadowstep: {
    id: 'shadowstep',
    name: 'Shadowstep',
    icon: '🌑',
    slot: 'boots',
    rarity: 'rare',
    description: 'Move silently through the shadows.',
    stats: {
      speed: 2,
      defense: 3,
    },
  },
  plateGreaves: {
    id: 'plateGreaves',
    name: 'Plate Greaves',
    icon: '👢',
    slot: 'boots',
    rarity: 'rare',
    description: 'Full plate protection for your legs.',
    stats: {
      speed: 1,
      defense: 6,
    },
  },

  // Epic Boots (3)
  windwalker: {
    id: 'windwalker',
    name: 'Windwalker',
    icon: '💨',
    slot: 'boots',
    rarity: 'epic',
    description: 'Walk on air itself with these mystical boots.',
    stats: {
      speed: 3,
      defense: 6,
    },
  },
  titanGreaves: {
    id: 'titanGreaves',
    name: 'Titan Greaves',
    icon: '⚡',
    slot: 'boots',
    rarity: 'epic',
    description: 'Each step shakes the earth.',
    stats: {
      speed: 2,
      defense: 9,
    },
  },
  voidwalkers: {
    id: 'voidwalkers',
    name: 'Voidwalkers',
    icon: '🌌',
    slot: 'boots',
    rarity: 'epic',
    description: 'Step between dimensions with ease.',
    stats: {
      speed: 3,
      defense: 7,
    },
  },

  // Legendary Boots (3)
  mercuryWings: {
    id: 'mercuryWings',
    name: 'Mercury Wings',
    icon: '👢',
    slot: 'boots',
    rarity: 'legendary',
    description: 'Wings on your heels grant godlike speed.',
    stats: {
      speed: 4,
      defense: 10,
    },
  },
  infinityStride: {
    id: 'infinityStride',
    name: 'Infinity Stride',
    icon: '✨',
    slot: 'boots',
    rarity: 'legendary',
    description: 'Walk across universes in a single step.',
    stats: {
      speed: 4,
      defense: 11,
    },
  },
  eternalsole: {
    id: 'eternalsole',
    name: 'Eternalsole',
    icon: '🌟',
    slot: 'boots',
    rarity: 'legendary',
    description: 'These boots will never wear out.',
    stats: {
      speed: 3,
      defense: 13,
    },
  },
};

// ============================================================
// OFFHAND (10 total: 2 common, 2 uncommon, 2 rare, 2 epic, 2 legendary)
// ============================================================
export const OFFHAND = {
  // Common Offhand (2)
  woodenShield: {
    id: 'woodenShield',
    name: 'Wooden Shield',
    icon: '🛡️',
    slot: 'offhand',
    rarity: 'common',
    description: 'A basic wooden shield, sturdy but worn.',
    stats: {
      defense: 2,
      health: 10,
    },
  },
  brokenBuckler: {
    id: 'brokenBuckler',
    name: 'Broken Buckler',
    icon: '🛡️',
    slot: 'offhand',
    rarity: 'common',
    description: 'A small, damaged buckler that still offers some protection.',
    stats: {
      defense: 1,
      health: 12,
    },
  },

  // Uncommon Offhand (2)
  steelShield: {
    id: 'steelShield',
    name: 'Steel Shield',
    icon: '🛡️',
    slot: 'offhand',
    rarity: 'uncommon',
    description: 'A well-crafted steel shield.',
    stats: {
      defense: 4,
      health: 20,
    },
  },
  ironBuckler: {
    id: 'ironBuckler',
    name: 'Iron Buckler',
    icon: '🛡️',
    slot: 'offhand',
    rarity: 'uncommon',
    description: 'A solid iron buckler for quick parries.',
    stats: {
      defense: 3,
      health: 25,
    },
  },

  // Rare Offhand (2)
  dragonShield: {
    id: 'dragonShield',
    name: 'Dragon Shield',
    icon: '🐲',
    slot: 'offhand',
    rarity: 'rare',
    description: 'A shield emblazoned with a dragon emblem.',
    stats: {
      defense: 8,
      health: 45,
    },
  },
  crystalAegis: {
    id: 'crystalAegis',
    name: 'Crystal Aegis',
    icon: '💎',
    slot: 'offhand',
    rarity: 'rare',
    description: 'A magical shield made of living crystal.',
    stats: {
      defense: 7,
      health: 50,
    },
  },

  // Epic Offhand (2)
  titanShield: {
    id: 'titanShield',
    name: 'Titan Shield',
    icon: '⚜️',
    slot: 'offhand',
    rarity: 'epic',
    description: 'A massive shield forged by titans.',
    stats: {
      defense: 12,
      health: 75,
    },
  },
  voidBuckler: {
    id: 'voidBuckler',
    name: 'Void Buckler',
    icon: '🌑',
    slot: 'offhand',
    rarity: 'epic',
    description: 'A buckler that absorbs attacks into the void.',
    stats: {
      defense: 10,
      health: 85,
    },
  },

  // Legendary Offhand (2)
  aegisEternal: {
    id: 'aegisEternal',
    name: 'Aegis Eternal',
    icon: '🛡️',
    slot: 'offhand',
    rarity: 'legendary',
    description: 'An unbreakable shield blessed by the gods.',
    stats: {
      defense: 18,
      health: 130,
    },
  },
  cosmicBarrier: {
    id: 'cosmicBarrier',
    name: 'Cosmic Barrier',
    icon: '🌌',
    slot: 'offhand',
    rarity: 'legendary',
    description: 'A barrier woven from the fabric of space itself.',
    stats: {
      defense: 15,
      health: 150,
      evasion: 0.05,
    },
  },
};

// ============================================================
// CAPES (10 total: 2 common, 2 uncommon, 2 rare, 2 epic, 2 legendary)
// ============================================================
export const CAPES = {
  // Common Capes (2)
  tatteredCloak: {
    id: 'tatteredCloak',
    name: 'Tattered Cloak',
    icon: '🧥',
    slot: 'cape',
    rarity: 'common',
    description: 'A worn cloak, full of holes and patches.',
    stats: {
      speed: 0,
      defense: 1,
    },
  },
  woolCape: {
    id: 'woolCape',
    name: 'Wool Cape',
    icon: '🦸',
    slot: 'cape',
    rarity: 'common',
    description: 'A simple wool cape that keeps you warm.',
    stats: {
      speed: 1,
      defense: 1,
    },
  },

  // Uncommon Capes (2)
  silkCape: {
    id: 'silkCape',
    name: 'Silk Cape',
    icon: '🦸',
    slot: 'cape',
    rarity: 'uncommon',
    description: 'A flowing cape made of fine silk.',
    stats: {
      speed: 1,
      defense: 2,
    },
  },
  huntersCloak: {
    id: 'huntersCloak',
    name: "Hunter's Cloak",
    icon: '🧥',
    slot: 'cape',
    rarity: 'uncommon',
    description: 'A practical cloak favored by hunters.',
    stats: {
      speed: 1,
      defense: 3,
    },
  },

  // Rare Capes (2)
  shadowCloak: {
    id: 'shadowCloak',
    name: 'Shadow Cloak',
    icon: '🌑',
    slot: 'cape',
    rarity: 'rare',
    description: 'A cloak woven from shadows themselves.',
    stats: {
      speed: 2,
      defense: 4,
    },
  },
  featheredMantle: {
    id: 'featheredMantle',
    name: 'Feathered Mantle',
    icon: '🦅',
    slot: 'cape',
    rarity: 'rare',
    description: 'A majestic mantle adorned with phoenix feathers.',
    stats: {
      speed: 2,
      defense: 5,
    },
  },

  // Epic Capes (2)
  stormcloak: {
    id: 'stormcloak',
    name: 'Stormcloak',
    icon: '⚡',
    slot: 'cape',
    rarity: 'epic',
    description: 'A cape crackling with lightning energy.',
    stats: {
      speed: 3,
      defense: 7,
      evasion: 0.03,
    },
  },
  phoenixMantle: {
    id: 'phoenixMantle',
    name: 'Phoenix Mantle',
    icon: '🔥',
    slot: 'cape',
    rarity: 'epic',
    description: 'A mantle woven from phoenix fire feathers.',
    stats: {
      speed: 2,
      defense: 8,
      health: 50,
    },
  },

  // Legendary Capes (2)
  voidweaver: {
    id: 'voidweaver',
    name: 'Voidweaver',
    icon: '🌌',
    slot: 'cape',
    rarity: 'legendary',
    description: 'A cape that bends reality around its wearer.',
    stats: {
      speed: 4,
      defense: 10,
      evasion: 0.05,
    },
  },
  cloakOfStars: {
    id: 'cloakOfStars',
    name: 'Cloak of Stars',
    icon: '✨',
    slot: 'cape',
    rarity: 'legendary',
    description: 'Woven from the fabric of the night sky itself.',
    stats: {
      speed: 3,
      defense: 12,
      critChance: 0.08,
    },
  },
};

// ============================================================
// BOTTOMS (10 total: 2 common, 2 uncommon, 2 rare, 2 epic, 2 legendary)
// ============================================================
export const BOTTOMS = {
  // Common Bottoms (2)
  clothPants: {
    id: 'clothPants',
    name: 'Cloth Pants',
    icon: '👖',
    slot: 'bottom',
    rarity: 'common',
    description: 'Simple cloth pants, comfortable but not protective.',
    stats: {
      defense: 1,
      health: 8,
    },
  },
  leatherTrousers: {
    id: 'leatherTrousers',
    name: 'Leather Trousers',
    icon: '👖',
    slot: 'bottom',
    rarity: 'common',
    description: 'Tough leather trousers for basic protection.',
    stats: {
      defense: 2,
      health: 10,
    },
  },

  // Uncommon Bottoms (2)
  chainLeggings: {
    id: 'chainLeggings',
    name: 'Chain Leggings',
    icon: '⛓️',
    slot: 'bottom',
    rarity: 'uncommon',
    description: 'Leggings reinforced with metal chains.',
    stats: {
      defense: 4,
      health: 20,
    },
  },
  studdedPants: {
    id: 'studdedPants',
    name: 'Studded Pants',
    icon: '👖',
    slot: 'bottom',
    rarity: 'uncommon',
    description: 'Leather pants with metal studs.',
    stats: {
      defense: 3,
      health: 25,
    },
  },

  // Rare Bottoms (2)
  plateGreaves: {
    id: 'plateGreaves',
    name: 'Plate Greaves',
    icon: '🦵',
    slot: 'bottom',
    rarity: 'rare',
    description: 'Heavy plate leg armor.',
    stats: {
      defense: 7,
      health: 40,
    },
  },
  dragonScaleLeggings: {
    id: 'dragonScaleLeggings',
    name: 'Dragon Scale Leggings',
    icon: '🐉',
    slot: 'bottom',
    rarity: 'rare',
    description: 'Leggings crafted from dragon scales.',
    stats: {
      defense: 6,
      health: 45,
    },
  },

  // Epic Bottoms (2)
  titanLeggings: {
    id: 'titanLeggings',
    name: 'Titan Leggings',
    icon: '⚜️',
    slot: 'bottom',
    rarity: 'epic',
    description: 'Leg armor forged by titans.',
    stats: {
      defense: 12,
      health: 80,
    },
  },
  abyssalGreaves: {
    id: 'abyssalGreaves',
    name: 'Abyssal Greaves',
    icon: '🌑',
    slot: 'bottom',
    rarity: 'epic',
    description: 'Dark greaves that absorb damage.',
    stats: {
      defense: 10,
      health: 90,
      speed: 1,
    },
  },

  // Legendary Bottoms (2)
  eternityPants: {
    id: 'eternityPants',
    name: 'Eternity Pants',
    icon: '✨',
    slot: 'bottom',
    rarity: 'legendary',
    description: 'Leg armor that transcends time itself.',
    stats: {
      defense: 18,
      health: 140,
    },
  },
  voidTrousers: {
    id: 'voidTrousers',
    name: 'Void Trousers',
    icon: '🌌',
    slot: 'bottom',
    rarity: 'legendary',
    description: 'Woven from the fabric between dimensions.',
    stats: {
      defense: 15,
      health: 160,
      evasion: 0.03,
    },
  },
};

// ============================================================
// GLOVES (10 total: 2 common, 2 uncommon, 2 rare, 2 epic, 2 legendary)
// ============================================================
export const GLOVES = {
  // Common Gloves (2)
  clothGloves: {
    id: 'clothGloves',
    name: 'Cloth Gloves',
    icon: '🧤',
    slot: 'gloves',
    rarity: 'common',
    description: 'Simple cloth gloves for basic hand protection.',
    stats: {
      attack: 1,
      critChance: 0.01,
    },
  },
  leatherGloves: {
    id: 'leatherGloves',
    name: 'Leather Gloves',
    icon: '🧤',
    slot: 'gloves',
    rarity: 'common',
    description: 'Sturdy leather gloves with a good grip.',
    stats: {
      attack: 2,
      critChance: 0.02,
    },
  },

  // Uncommon Gloves (2)
  chainGauntlets: {
    id: 'chainGauntlets',
    name: 'Chain Gauntlets',
    icon: '🥊',
    slot: 'gloves',
    rarity: 'uncommon',
    description: 'Metal gauntlets with chainmail backing.',
    stats: {
      attack: 3,
      critChance: 0.04,
      defense: 1,
    },
  },
  studdedGloves: {
    id: 'studdedGloves',
    name: 'Studded Gloves',
    icon: '🧤',
    slot: 'gloves',
    rarity: 'uncommon',
    description: 'Leather gloves with metal studs for extra punch.',
    stats: {
      attack: 4,
      critChance: 0.03,
    },
  },

  // Rare Gloves (2)
  plateGauntlets: {
    id: 'plateGauntlets',
    name: 'Plate Gauntlets',
    icon: '🥊',
    slot: 'gloves',
    rarity: 'rare',
    description: 'Heavy plate gauntlets for crushing blows.',
    stats: {
      attack: 6,
      critChance: 0.06,
      defense: 3,
    },
  },
  dragonClaws: {
    id: 'dragonClaws',
    name: 'Dragon Claws',
    icon: '🐉',
    slot: 'gloves',
    rarity: 'rare',
    description: 'Gauntlets tipped with actual dragon claws.',
    stats: {
      attack: 7,
      critChance: 0.08,
      critDamage: 0.15,
    },
  },

  // Epic Gloves (2)
  titanGauntlets: {
    id: 'titanGauntlets',
    name: 'Titan Gauntlets',
    icon: '⚜️',
    slot: 'gloves',
    rarity: 'epic',
    description: 'Massive gauntlets that crush anything they grip.',
    stats: {
      attack: 10,
      critChance: 0.10,
      defense: 6,
    },
  },
  voidGrips: {
    id: 'voidGrips',
    name: 'Void Grips',
    icon: '🌑',
    slot: 'gloves',
    rarity: 'epic',
    description: 'Gauntlets that channel void energy into each strike.',
    stats: {
      attack: 12,
      critChance: 0.12,
      critDamage: 0.25,
    },
  },

  // Legendary Gloves (2)
  infinityGrasp: {
    id: 'infinityGrasp',
    name: 'Infinity Grasp',
    icon: '✨',
    slot: 'gloves',
    rarity: 'legendary',
    description: 'Gauntlets that hold the power of infinity.',
    stats: {
      attack: 16,
      critChance: 0.15,
      critDamage: 0.40,
      defense: 8,
    },
  },
  cosmicClaws: {
    id: 'cosmicClaws',
    name: 'Cosmic Claws',
    icon: '🌌',
    slot: 'gloves',
    rarity: 'legendary',
    description: 'Claws forged from the essence of dying stars.',
    stats: {
      attack: 18,
      critChance: 0.18,
      critDamage: 0.50,
    },
  },
};

// ============================================================
// BELTS (10 total: 2 common, 2 uncommon, 2 rare, 2 epic, 2 legendary)
// ============================================================
export const BELTS = {
  // Common Belts (2)
  ropeBelt: {
    id: 'ropeBelt',
    name: 'Rope Belt',
    icon: '🪢',
    slot: 'belt',
    rarity: 'common',
    description: 'A simple rope tied around the waist.',
    stats: {
      health: 5,
      defense: 1,
    },
  },
  leatherBelt: {
    id: 'leatherBelt',
    name: 'Leather Belt',
    icon: '⚙️',
    slot: 'belt',
    rarity: 'common',
    description: 'A sturdy leather belt with a brass buckle.',
    stats: {
      health: 8,
      defense: 1,
    },
  },

  // Uncommon Belts (2)
  studdedBelt: {
    id: 'studdedBelt',
    name: 'Studded Belt',
    icon: '⚙️',
    slot: 'belt',
    rarity: 'uncommon',
    description: 'A reinforced belt with metal studs.',
    stats: {
      health: 15,
      defense: 2,
    },
  },
  chainBelt: {
    id: 'chainBelt',
    name: 'Chain Belt',
    icon: '⛓️',
    slot: 'belt',
    rarity: 'uncommon',
    description: 'A belt made of interlocking chains.',
    stats: {
      health: 12,
      defense: 3,
    },
  },

  // Rare Belts (2)
  runicBelt: {
    id: 'runicBelt',
    name: 'Runic Belt',
    icon: '📿',
    slot: 'belt',
    rarity: 'rare',
    description: 'A belt inscribed with powerful runes.',
    stats: {
      health: 25,
      defense: 5,
    },
  },
  dragonBelt: {
    id: 'dragonBelt',
    name: 'Dragon Belt',
    icon: '🐲',
    slot: 'belt',
    rarity: 'rare',
    description: 'A belt made from dragon hide.',
    stats: {
      health: 30,
      defense: 4,
    },
  },

  // Epic Belts (2)
  titanBelt: {
    id: 'titanBelt',
    name: 'Titan Belt',
    icon: '⚜️',
    slot: 'belt',
    rarity: 'epic',
    description: 'A massive belt forged by titan smiths.',
    stats: {
      health: 55,
      defense: 8,
    },
  },
  voidSash: {
    id: 'voidSash',
    name: 'Void Sash',
    icon: '🌑',
    slot: 'belt',
    rarity: 'epic',
    description: 'A sash woven from threads of the void.',
    stats: {
      health: 65,
      defense: 6,
      speed: 1,
    },
  },

  // Legendary Belts (2)
  eternityGirdle: {
    id: 'eternityGirdle',
    name: 'Eternity Girdle',
    icon: '✨',
    slot: 'belt',
    rarity: 'legendary',
    description: 'A belt that holds the power of eternity.',
    stats: {
      health: 100,
      defense: 12,
    },
  },
  cosmicCord: {
    id: 'cosmicCord',
    name: 'Cosmic Cord',
    icon: '🌌',
    slot: 'belt',
    rarity: 'legendary',
    description: 'A cord that binds fate itself.',
    stats: {
      health: 85,
      defense: 10,
      critChance: 0.05,
    },
  },
};

// ============================================================
// PENDANTS (10 total: 2 common, 2 uncommon, 2 rare, 2 epic, 2 legendary)
// Note: PlayerPanel expects 'pendant1' slot
// ============================================================
export const PENDANTS = {
  // Common Pendants (2)
  woodenPendant: {
    id: 'woodenPendant',
    name: 'Wooden Pendant',
    icon: '📿',
    slot: 'pendant1',
    rarity: 'common',
    description: 'A simple carved wooden pendant.',
    stats: {
      critChance: 0.01,
      health: 5,
    },
  },
  copperAmulet: {
    id: 'copperAmulet',
    name: 'Copper Amulet',
    icon: '💎',
    slot: 'pendant1',
    rarity: 'common',
    description: 'A tarnished copper amulet.',
    stats: {
      critChance: 0.02,
      health: 8,
    },
  },

  // Uncommon Pendants (2)
  silverAmulet: {
    id: 'silverAmulet',
    name: 'Silver Amulet',
    icon: '💎',
    slot: 'pendant1',
    rarity: 'uncommon',
    description: 'A polished silver amulet.',
    stats: {
      critChance: 0.04,
      critDamage: 0.10,
      health: 15,
    },
  },
  jadePendant: {
    id: 'jadePendant',
    name: 'Jade Pendant',
    icon: '💚',
    slot: 'pendant1',
    rarity: 'uncommon',
    description: 'A pendant carved from precious jade.',
    stats: {
      critChance: 0.05,
      critDamage: 0.08,
      health: 12,
    },
  },

  // Rare Pendants (2)
  rubyAmulet: {
    id: 'rubyAmulet',
    name: 'Ruby Amulet',
    icon: '💎',
    slot: 'pendant1',
    rarity: 'rare',
    description: 'A brilliant ruby set in gold.',
    stats: {
      critChance: 0.08,
      critDamage: 0.20,
      health: 25,
    },
  },
  sapphirePendant: {
    id: 'sapphirePendant',
    name: 'Sapphire Pendant',
    icon: '💙',
    slot: 'pendant1',
    rarity: 'rare',
    description: 'A deep blue sapphire pendant.',
    stats: {
      critChance: 0.09,
      critDamage: 0.18,
      health: 28,
    },
  },

  // Epic Pendants (2)
  starCore: {
    id: 'starCore',
    name: 'Star Core',
    icon: '⭐',
    slot: 'pendant1',
    rarity: 'epic',
    description: 'The core of a fallen star, still burning with power.',
    stats: {
      critChance: 0.12,
      critDamage: 0.35,
      health: 40,
    },
  },
  abyssalHeart: {
    id: 'abyssalHeart',
    name: 'Abyssal Heart',
    icon: '🖤',
    slot: 'pendant1',
    rarity: 'epic',
    description: 'A heart torn from an abyssal creature.',
    stats: {
      critChance: 0.14,
      critDamage: 0.40,
      health: 35,
    },
  },

  // Legendary Pendants (2)
  heartOfInfinity: {
    id: 'heartOfInfinity',
    name: 'Heart of Infinity',
    icon: '💜',
    slot: 'pendant1',
    rarity: 'legendary',
    description: 'Contains infinite power within its core.',
    stats: {
      critChance: 0.18,
      critDamage: 0.60,
      health: 70,
    },
  },
  voidEssencePendant: {
    id: 'voidEssencePendant',
    name: 'Void Essence Pendant',
    icon: '🌌',
    slot: 'pendant1',
    rarity: 'legendary',
    description: 'Crystallized essence of the void itself.',
    stats: {
      critChance: 0.20,
      critDamage: 0.70,
      health: 60,
    },
  },
};

// ============================================================
// RINGS (15 total: 3 per rarity)
// Note: PlayerPanel expects 'ring1' and 'ring2' slots
// Rings can be equipped in either slot
// ============================================================
export const RINGS = {
  // Common Rings (3)
  copperRing: {
    id: 'copperRing',
    name: 'Copper Ring',
    icon: '💍',
    slot: 'ring1',
    rarity: 'common',
    description: 'A simple band of copper.',
    stats: {
      critChance: 0.02,
      health: 5,
    },
  },
  glassOrb: {
    id: 'glassOrb',
    name: 'Glass Orb',
    icon: '🔮',
    slot: 'ring1',
    rarity: 'common',
    description: 'A fragile orb with faint magical energy.',
    stats: {
      critChance: 0.03,
      health: 3,
    },
  },
  woodenCharm: {
    id: 'woodenCharm',
    name: 'Wooden Charm',
    icon: '📿',
    slot: 'ring1',
    rarity: 'common',
    description: 'A lucky charm carved from oak.',
    stats: {
      critChance: 0.01,
      health: 8,
    },
  },

  // Uncommon Rings (3)
  silverRing: {
    id: 'silverRing',
    name: 'Silver Ring',
    icon: '💍',
    slot: 'ring1',
    rarity: 'uncommon',
    description: 'A ring of polished silver.',
    stats: {
      critChance: 0.05,
      critDamage: 0.12,
      health: 12,
    },
  },
  crystalPendant: {
    id: 'crystalPendant',
    name: 'Crystal Pendant',
    icon: '💎',
    slot: 'ring1',
    rarity: 'uncommon',
    description: 'A pendant with a glowing crystal.',
    stats: {
      critChance: 0.06,
      critDamage: 0.10,
      health: 15,
    },
  },
  runeStone: {
    id: 'runeStone',
    name: 'Rune Stone',
    icon: '🔮',
    slot: 'ring1',
    rarity: 'uncommon',
    description: 'Carved with ancient protective runes.',
    stats: {
      critChance: 0.04,
      critDamage: 0.15,
      health: 10,
    },
  },

  // Rare Rings (3)
  goldRing: {
    id: 'goldRing',
    name: 'Gold Ring',
    icon: '💍',
    slot: 'ring1',
    rarity: 'rare',
    description: 'Pure gold infused with magic.',
    stats: {
      critChance: 0.09,
      critDamage: 0.28,
      health: 25,
    },
  },
  sapphireAmulet: {
    id: 'sapphireAmulet',
    name: 'Sapphire Amulet',
    icon: '💎',
    slot: 'ring1',
    rarity: 'rare',
    description: 'A brilliant blue gemstone of power.',
    stats: {
      critChance: 0.10,
      critDamage: 0.25,
      health: 28,
    },
  },
  arcaneBeads: {
    id: 'arcaneBeads',
    name: 'Arcane Beads',
    icon: '📿',
    slot: 'ring1',
    rarity: 'rare',
    description: 'Prayer beads charged with mystical energy.',
    stats: {
      critChance: 0.08,
      critDamage: 0.30,
      health: 22,
    },
  },

  // Epic Rings (3)
  dragonEye: {
    id: 'dragonEye',
    name: 'Dragon Eye',
    icon: '👁️',
    slot: 'ring1',
    rarity: 'epic',
    description: 'The eye of an ancient dragon, still watching.',
    stats: {
      critChance: 0.15,
      critDamage: 0.55,
      health: 48,
    },
  },
  starFragment: {
    id: 'starFragment',
    name: 'Star Fragment',
    icon: '⭐',
    slot: 'ring1',
    rarity: 'epic',
    description: 'A piece of a fallen star.',
    stats: {
      critChance: 0.16,
      critDamage: 0.50,
      health: 52,
    },
  },
  voidCrystal: {
    id: 'voidCrystal',
    name: 'Void Crystal',
    icon: '🌑',
    slot: 'ring1',
    rarity: 'epic',
    description: 'Crystallized essence of the void.',
    stats: {
      critChance: 0.14,
      critDamage: 0.60,
      health: 45,
    },
  },

  // Legendary Rings (3)
  heartOfCosmos: {
    id: 'heartOfCosmos',
    name: 'Heart of Cosmos',
    icon: '💫',
    slot: 'ring1',
    rarity: 'legendary',
    description: 'The beating heart of the universe itself.',
    stats: {
      critChance: 0.22,
      critDamage: 1.20,
      health: 85,
    },
  },
  ringOfEternity: {
    id: 'ringOfEternity',
    name: 'Ring of Eternity',
    icon: '💍',
    slot: 'ring1',
    rarity: 'legendary',
    description: 'Grants power beyond mortal comprehension.',
    stats: {
      critChance: 0.25,
      critDamage: 1.00,
      health: 92,
    },
  },
  divineRelic: {
    id: 'divineRelic',
    name: 'Divine Relic',
    icon: '✨',
    slot: 'ring1',
    rarity: 'legendary',
    description: 'A relic touched by the gods.',
    stats: {
      critChance: 0.20,
      critDamage: 1.40,
      health: 78,
    },
  },
};

// ============================================================
// AGGREGATED EXPORTS
// ============================================================

// All equipment by slot
export const EQUIPMENT_BY_SLOT = {
  weapon: WEAPONS,
  offhand: OFFHAND,
  helmet: HELMETS,
  cape: CAPES,
  top: ARMOR,
  bottom: BOTTOMS,
  gloves: GLOVES,
  boots: BOOTS,
  belt: BELTS,
  pendant1: PENDANTS,
  ring1: RINGS,
};

// All equipment in one object
export const ALL_EQUIPMENT = {
  ...WEAPONS,
  ...OFFHAND,
  ...HELMETS,
  ...CAPES,
  ...ARMOR,
  ...BOTTOMS,
  ...GLOVES,
  ...BOOTS,
  ...BELTS,
  ...PENDANTS,
  ...RINGS,
};

// Helper functions
export function getEquipmentBySlot(slot) {
  return EQUIPMENT_BY_SLOT[slot] || {};
}

export function getEquipmentById(id) {
  return ALL_EQUIPMENT[id] || null;
}

export function getEquipmentByRarity(rarity) {
  return Object.values(ALL_EQUIPMENT).filter(item => item.rarity === rarity);
}

export function getEquipmentBySlotAndRarity(slot, rarity) {
  const slotItems = EQUIPMENT_BY_SLOT[slot] || {};
  return Object.values(slotItems).filter(item => item.rarity === rarity);
}
